<script setup>
    import SessionsGridList from '~/components/blocks/two-six/sessions/grid-list.vue';

    const dayjs = useDayjs();

    const props = defineProps({
        schedule: {
            type: Object,
            default: () => ({}),
        },
        view: {
            type: String,
            default: 'list',
        },
    });

    const enrichedSchedule = ref({});
    const isLoading = ref(true);
    const error = ref(null);
    const showSessionDetails = ref(false);
    const selectedSession = ref(null);

    // Format day display
    const formatDayDisplay = (dayString) => {
        // Parse the date string as a date in PDT timezone (not converting from another timezone)
        return dayjs.tz(dayString, 'America/Los_Angeles').format('dddd, MMMM D, YYYY');
    };

    // Fetch rich session data
    const fetchSessionData = async () => {
        try {
            isLoading.value = true;
            error.value = null;

            // Collect all session IDs from all days
            const allSessionIds = [];
            Object.values(props.schedule).forEach(daySessions => {
                daySessions.forEach(session => {
                    if (session.id && !allSessionIds.includes(session.id)) {
                        allSessionIds.push(session.id);
                    }
                });
            });

            if (allSessionIds.length === 0) {
                enrichedSchedule.value = props.schedule;
                isLoading.value = false;
                return;
            }

            // Fetch rich data for all sessions at once
            const response = await fetch(`/api/v1/sessions?id=${allSessionIds.join(',')}`);
            if (!response.ok) {
                throw new Error('Failed to fetch session data');
            }

            const data = await response.json();
            const richSessions = data.sessions || [];

            // Create a lookup map for quick access
            const sessionLookup = {};
            richSessions.forEach(session => {
                sessionLookup[session.id] = session;
            });

            // Enrich the schedule with rich session data
            const enriched = {};
            Object.entries(props.schedule).forEach(([day, sessions]) => {
                enriched[day] = sessions.map(session => {
                    const richData = sessionLookup[session.id];
                    if (richData) {
                        return {
                            ...session,
                            ...richData,
                            // Keep the original timing from agenda builder
                            agendaStartTime: session.startTime,
                            agendaEndTime: session.endTime,
                            reason: session.reason,
                            recommendationReason: session.recommendationReason,
                            score: session.score || richData.relevanceScore || 0,
                        };
                    }
                    return session; // Fallback to basic data if rich data not found
                });
            });

            enrichedSchedule.value = enriched;
        } catch (err) {
            console.error('Error fetching session data:', err);
            error.value = err.message;
            // Fallback to basic schedule if enrichment fails
            enrichedSchedule.value = props.schedule;
        } finally {
            isLoading.value = false;
        }
    };

    const handleSessionClick = (session) => {
        console.log('Session clicked:', session);
        selectedSession.value = session;
        showSessionDetails.value = true;
    };

    // Watch for changes in schedule prop
    watch(() => props.schedule, fetchSessionData, { immediate: true });

</script>
<template>
    <div class="assistant-schedule">
        <div v-if="isLoading" class="text-center text-(--teal)">
            <p>Loading session details...</p>
        </div>
        <div v-else-if="error" class="text-center text-(--teal)">
            <p class="error">Error loading session details: {{ error }}</p>
        </div>
        <div v-else class="schedule-content">
            <SessionsByDay v-if="view === 'calendar'" :sessions="enrichedSchedule" disableLink @load="handleSessionClick" />
            <div v-else v-for="(sessions, day) in enrichedSchedule" :key="day" class="flex flex-col gap-[1.4375rem] [&:not(:last-child)]:mb-[1.4375rem]">
                <div class="bg-(--orange-dark) text-(--cream) px-(--box-padding-lr) py-5 font-medium w-full rounded-(--radius)">
                    {{ formatDayDisplay(day) }}
                </div>
                <SessionsGridList
                    :data="{ sessions: sessions, pages: 1, current_page: 1 }"
                    :loading="false"
                    :showFullCard="true"
                    disableLink
                    @load="handleSessionClick"
                />
            </div>
        </div>
        <FragmentsDrawersSession :session_id="(selectedSession?.unique_id || selectedSession?.id || '').toLowerCase()" v-model:open="showSessionDetails" />
    </div>
</template>
